import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isSameDay, startOfDay, isBefore } from "date-fns"
import _ from 'lodash';
import Input from '../../Input';
import {
  ButtonType,
  InputType,
  LoaderTypes,
} from '../../../utils/@globalTypes';
import classNames from 'classnames';

import { FormikProps, useFormikContext } from 'formik';
import {
  BookingFormType,
  FormBookingType,
  PriceRequestType,
} from '../../../redux/types/bookingTypes';
import styles from './PriceBlock.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RentalObjectSelectors } from '../../../redux/reducers/rentalObjectSlice';
import Button from '../../Button';
import { TariffsSelectors } from '../../../redux/reducers/tariffsSlice';
import { TarifTypes } from '../../../redux/types/@types';
import { CURRENCY, priceMask, summMask } from '../../../utils/constants';
import { formatDate } from '../functions';
import {
  BookingSelectors,
  clearPriceError,
  getTotalPrice,
  setPriceError,
} from '../../../redux/reducers/bookingSlice';
import { PageSelectors } from '../../../redux/reducers/pageSlice';
import Loader from '../../Loader';

type PriceBlockProps = {
  windowType?: BookingFormType;
  formik: FormikProps<FormBookingType>;
};

const PriceBlock = ({
  windowType = BookingFormType.view,
  formik,
}: PriceBlockProps) => {
  const { setFieldValue } = useFormikContext();
  const {
    handleChange,
    values,
    errors,
    setFieldTouched,
    touched,
    validateField,
    resetForm,
    initialValues,
    dirty
  } = formik;
  const dispatch = useDispatch();

  const current_object = useSelector(
    RentalObjectSelectors.getCurrentRentalObject,
  );
  const currentTariff = useSelector(TariffsSelectors.getCurrentTarif);
  const isLoading = useSelector(
    PageSelectors.getLoadersData(LoaderTypes.BOOKING_PAGE),
  );

  const priceResponse = useSelector(BookingSelectors.getBookingPrice);
  const priceError = useSelector(BookingSelectors.getPriceError);

  const message = useSelector(PageSelectors.getMessage);
  const contentRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number | null>(null);

  const [isDataChanged, setIsDataChanged] = useState<boolean>(false)
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false)

  const [dateError, setDateError] = useState<string>("")

  // Флаг, что сейчас идёт программное обновление из ответа бэка
  const isUpdatingFromResponse = useRef(false); 

  useEffect(() => { 
    if(windowType===BookingFormType.edit &&
      isBefore(values.check_in_date,startOfDay(new Date()))){
        setDateError("Нельзя пересчитать стоимость на прошедшие даты, введите суммы вручную")
      } else {
        setDateError("")
      }
    }, [values, windowType]); 


    
  useEffect(() => {    
    if (Number(values.total) === Number(values.snapTotal) && !isDataChanged 
    
     ) {      
      setIsButtonDisabled (true)
     } else {      
      setIsButtonDisabled (false)
     }
  }, [values, isDataChanged]);

  const getFullHeight = (element: HTMLElement) => {
    const styles = window.getComputedStyle(element);
    const marginTop = parseFloat(styles.marginTop);
    const marginBottom = parseFloat(styles.marginBottom);
    const height = element.offsetHeight;
    return height + marginTop + marginBottom;
  };

  useLayoutEffect(() => {  // устанавливаем высоту блока чтобы при лоадере не прыгала
    if (!isLoading && values && contentRef.current) {
      const height = getFullHeight(contentRef.current);
      setContainerHeight(height);
    }
  }, [isLoading, values]);

  useEffect(() => {
    //принудительная очистка формы для сброса свойства dirty
    if (Object.keys(touched).length === 0) {
      resetForm();
    }
  }, []);
  
  const handleClickSumm = async () => {
    // пересчет суммы брони при клике на кнопку

    if (
      values.check_in_date &&
      values.check_out_date &&
      current_object &&
      !errors.check_in_date &&
      !errors.check_out_date
    ) {
      
      const dataRequest: PriceRequestType = {
        rental_object: current_object.id, 
        check_in_date: formatDate(values.check_in_date),
        check_out_date: formatDate(values.check_out_date),
        adults: values.adult,
        children: values.children || [],
        animals_count: Number(values.animals_count),
      };
      try {
        dispatch(getTotalPrice(dataRequest));   
        setIsDataChanged(false) 
      } catch{
        // console.log("ошибка")
      }
        
  }}
  
  useEffect(() => {
    if (priceResponse && !isLoading) {

      isUpdatingFromResponse.current = true;
      setFieldTouched('living_costs');
      setFieldValue('living_costs', priceResponse.accommodation_price);
      setFieldValue('animals_costs', priceResponse.total_animal_price);
      setFieldValue('meals_costs', priceResponse.total_board_price); 

      const total = (Number(priceResponse.accommodation_price) + 
      Number(priceResponse.total_board_price) + 
      Number(priceResponse.total_animal_price)).toFixed(2).toString()

      setFieldValue("snapTotal", total);
    }
  }, [priceResponse, isLoading]);



  useEffect(() => {
    if (!values.has_animals && Number(values.animals_costs) > 0) {
      setFieldValue('animals_costs', '');
    }
  }, [values]);

  useEffect(() => {
    
    setFieldValue(
        'total',
        (
          (values.living_costs ? Number(values.living_costs) : 0) +
          (values.meals_costs ? Number(values.meals_costs) : 0) +
          (values.animals_costs ? Number(values.animals_costs) : 0)
        )
          .toFixed(2)
          .toString(),
      );

  }, [values.living_costs, values.meals_costs, values.animals_costs]);

  useEffect(() => {
    setFieldValue(
        'rest',
        (
          (values.total ? Number(values.total) : 0) -
          (values.paid ? Number(values.paid) : 0)
        )
          .toFixed(2)
          .toString(),
      );
  }, [values.total, values.paid]);

  useEffect(() => {
    if (touched.meals_costs || touched.living_costs || touched.paid ) {     
      validateField('total');
      validateField('meals_costs');
      validateField('living_costs');
      validateField('animals_costs');
      validateField('paid');
      validateField('rest');      
    }
  }, [touched, values, priceResponse]);

  useEffect(() => {
    if(windowType===BookingFormType.new && Number(values.total)>0){
      setIsDataChanged(true)     
    }
  }, [values.rental_object, values.check_in_date, values.check_out_date, values.adult, values.children, values.animals_count]);

  useEffect(() => {
    if(windowType===BookingFormType.edit && (
        values.rental_object !== initialValues.rental_object ||
        !isSameDay(values.check_in_date, initialValues.check_in_date) ||
        !isSameDay(values.check_out_date, initialValues.check_out_date) ||
        values.animals_count !== initialValues.animals_count)
      ){
      setIsDataChanged(true)      
    }
  }, [values.rental_object, values.check_in_date, values.check_out_date, values.adult, values.children, values.animals_count]);

  useEffect(() => {
    if (isDataChanged)
      setIsDataChanged(false)   
    // if(priceError && values.total) 
    //   dispatch(clearPriceError({}))
  }, [values.total]);

  useEffect(() => {
    priceError && dispatch(clearPriceError({}))
  }, [values.rental_object]);

  return (
    <>
      <div className={styles.priceBlock}>
        <div className={styles.summBlock}>
          <div className={styles.totalLine}>
            <span className={styles.totalTitle}>
              Общая стоимость за выбранный период
            </span>
            <div className={styles.totalTitleMob}>Общая стоимость</div>
            <div className={styles.inputLine}>
              <div className={styles.inputsumm}>
                <Input
                  id="total"
                  placeholder="0.00"
                  type={InputType.MASK}
                  mask={summMask}
                  value={values.total}
                  onChange={handleChange('total')}
                  onBlur={() => setFieldTouched('total')}
                  isBooking={true}
                  disabled={true}
                />
              </div>
              <span className={styles.currency}>{CURRENCY}</span>
            </div>
          </div>
          {errors.total && (
            <div className={classNames(styles.errorText, styles.summError)}>
              {errors.total}
            </div>
          )}
          <div ref={contentRef} style={{ 
          minHeight: containerHeight || 'auto',
          transition: 'min-height 0.1s ease' // Плавный переход
        }}>
          {isLoading ? (
            <Loader />
          ) : (
            <div className={styles.summLines}>
              <div className={classNames(styles.payLine, styles.leave)}>
                В том числе:
                <div className={styles.inputLine}>
                  <span className={styles.inputsumm}>
                    <Input
                      id="living_costs"
                      placeholder="0.00"
                      type={InputType.MASK}
                      mask={priceMask}
                      value={values.living_costs || ''}
                      onChange={handleChange('living_costs')}
                      onBlur={() => setFieldTouched('living_costs')}
                      disabled={windowType === BookingFormType.view}
                      isBooking={true}
                    />
                  </span>
                  <span className={styles.currency}>{CURRENCY}</span>
                </div>
                за проживание *
              </div>
              {errors.living_costs && (
                <div className={classNames(styles.errorText, styles.summError)}>
                  {errors.living_costs}
                </div>
              )}
              <div className={classNames(styles.payLine, styles.meal)}>
                <div className={styles.inputLine}>
                  <span className={styles.inputsumm}>
                    <Input
                      id="meals_costs"
                      placeholder="0.00"
                      type={InputType.MASK}
                      mask={priceMask}
                      value={values.meals_costs || ''}
                      onChange={handleChange('meals_costs')}
                      onBlur={() => setFieldTouched('meals_costs')}
                      disabled={windowType === BookingFormType.view}
                      isBooking={true}
                    />
                  </span>
                  <span className={styles.currency}>{CURRENCY}</span>
                </div>
                за питание
              </div>
              {values.has_animals && (
                <div className={classNames(styles.payLine, styles.meal)}>
                  <div className={styles.inputLine}>
                    <span className={styles.inputsumm}>
                      <Input
                        id="animals_costs"
                        placeholder="0.00"
                        type={InputType.MASK}
                        mask={priceMask}
                        value={values.animals_costs || ''}
                        onChange={handleChange('animals_costs')}
                        onBlur={() => setFieldTouched('animals_costs')}
                        disabled={
                          windowType === BookingFormType.view ||
                          !values.has_animals
                        }
                        isBooking={true}
                      />
                    </span>
                    <span className={styles.currency}>{CURRENCY}</span>
                  </div>
                  за животных
                </div>
              )}
            </div>
          )}
          </div>
          <ul className={styles.list}>
            <li className={styles.payLine}>
              <span className={styles.marker}>&#8226;</span>
              <div className={styles.payTitle}>Уже оплачено</div>
              <div className={styles.inputLine}>
                <span className={styles.inputsumm}>
                  <Input
                    id="paid"
                    placeholder="0.00"
                    type={InputType.MASK}
                    mask={priceMask}
                    value={values.paid || ''}
                    onChange={handleChange('paid')}
                    onBlur={() => setFieldTouched('paid')}
                    disabled={windowType === BookingFormType.view}
                    isBooking={true}
                  />
                </span>
                <span className={styles.currency}>{CURRENCY}</span>
              </div>
            </li>
            {errors.paid && (
              <span className={classNames(styles.errorText, styles.summError)}>
                {errors.paid}
              </span>
            )}
            <li className={styles.payLine}>
              <span className={styles.marker}>&#8226;</span>
              <span className={classNames(styles.payTitle, styles.rest)}>
                Сумма, которая осталась к оплате
              </span>
              <div className={styles.restMob}>Осталось к оплате</div>
              <div className={styles.inputLine}>
                <span className={styles.inputsumm}>
                  <Input
                    id="rest"
                    placeholder="0.00"
                    type={InputType.MASK}
                    mask={summMask}
                    value={values.rest || '0.00'}
                    onChange={handleChange('rest')}
                    disabled={true}
                    isBooking={true}
                  />
                </span>
                <span className={styles.currency}>{CURRENCY}</span>
              </div>
            </li>
          </ul>
        </div>
        
        <div className={styles.btnSumm}>
          {currentTariff !== TarifTypes.FREE && ( 
            <>
            <Button
            title="Пересчитать"
            type={ButtonType.PRIMARY_SMALL}
            onClick={handleClickSumm}
            disabled={
              windowType === BookingFormType.view ||
              !values.rental_object || priceError ||
              errors.adult || (values.has_animals && !values.animals_count) 
              || isLoading  || isButtonDisabled || dateError || errors.check_out_date             
                ? true
                : false
            }
          /> 
          {priceError && 
            <div className={classNames(styles.errorText, styles.message)}>   
              {priceError}
            </div>
          }
          {dateError && 
            <div className={classNames(styles.errorText, styles.message)}>   
              {dateError}
            </div>
          }
          </>)}
          {isDataChanged &&
            windowType !== BookingFormType.view &&
            !errors.check_out_date &&
            <div className={classNames(styles.errorText, styles.message)}>              
              <div>Данные были изменены, проверьте стоимость</div>
            </div>
          }
        </div>       
      </div>
    </>
  );
};

export default PriceBlock;
